
import { useState } from 'react';
import { caseStudies } from '../../mocks/portfolio';
import Button from '../../components/base/Button';

export default function Work() {
  const [selectedStudy, setSelectedStudy] = useState<typeof caseStudies[0] | null>(null);

  return (
    <div className="min-h-screen bg-gray-950 text-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Case Studies
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A comprehensive look at my design process, challenges faced, and solutions delivered across various
            industries and platforms.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-12 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <div
                key={study.id}
                className="group bg-gray-900 rounded-2xl overflow-hidden hover:scale-105 transition-all duration-500 cursor-pointer"
                onClick={() => setSelectedStudy(study)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-purple-400 font-medium">{study.category}</span>
                    <span className="text-sm text-gray-500">{study.year}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-purple-400 transition-colors duration-300">
                    {study.title}
                  </h3>
                  <p className="text-gray-400 mb-4 line-clamp-3">{study.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {study.tags.slice(0, 4).map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <i className="ri-arrow-right-line text-purple-400 group-hover:translate-x-1 transition-transform duration-300"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Modal */}
      {selectedStudy && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={() => setSelectedStudy(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white rounded-full transition-colors duration-300 cursor-pointer z-10"
              >
                <i className="ri-close-line text-xl"></i>
              </button>

              <div className="aspect-video overflow-hidden rounded-t-2xl">
                <img src={selectedStudy.image} alt={selectedStudy.title} className="w-full h-full object-cover" />
              </div>

              <div className="p-8">
                {/* Header Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-sm text-purple-400 font-medium bg-purple-400/10 px-3 py-1 rounded-full">
                        {selectedStudy.category}
                      </span>
                      <span className="text-sm text-gray-500">{selectedStudy.year}</span>
                    </div>

                    <h2 className="text-4xl font-bold mb-6">{selectedStudy.title}</h2>
                    <p className="text-gray-300 text-lg leading-relaxed mb-8">{selectedStudy.description}</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold mb-3">Project Details</h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-400">Role:</span>
                          <span className="text-white ml-2">{selectedStudy.role}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Year:</span>
                          <span className="text-white ml-2">{selectedStudy.year}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-white">Skills Used</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedStudy.tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Problem, Solution, Results */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <div className="bg-gray-800/50 rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 text-red-400">Problem</h3>
                    <p className="text-gray-300 leading-relaxed">{selectedStudy.challenge}</p>
                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 text-blue-400">Solution</h3>
                    <p className="text-gray-300 leading-relaxed">{selectedStudy.solution}</p>
                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 text-green-400">Results</h3>
                    <p className="text-gray-300 leading-relaxed">{selectedStudy.results}</p>
                  </div>
                </div>

                {/* Unique Content for Opportunity Feed Card (ID: 1) */}
                {selectedStudy.id === 1 && (
                  <div>
                    {/* Axiom's New Flow Section */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Axiom's New Flow</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          Axiom connects legal teams with top talent for everything from everyday in-house needs to complex counsel work. Over
                          time, it has evolved from an analog service to a digital platform while preserving its white-glove experience. The
                          Opportunity Feed marked the first step in empowering lawyers to take control of their careers.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/298f986c00af6002bb9f351f4499ba37.png"
                              alt="Talent Services Process Changes"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Talent Services Process Changes</h4>
                            <p className="text-gray-400 leading-relaxed">
                              With the introduction of the Opportunity Feed, Talent Services began listing opportunities directly, allowing talent to
                              express interest proactively rather than waiting for Talent Success to present opportunities.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8ccb4deef482a40694a223f7d2a63d22.png"
                              alt="User Flows"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Flows</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Reviewed and updated the master user flow to uncover issues and identify solutions resulting from the introduction of the
                              Opportunity Feed.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Research & Analysis Section */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Research & Analysis</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          The transition to a digital platform gave talent greater independence in managing their careers, but it also resulted a
                          bottleneck for Talent Services. Talents expressed interest in more opportunities and a wider range of opportunities, some of
                          which they did not qualify for.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8d89e211b26dad1f954b94093a3071cf.png"
                              alt="Data Analysis Metrics"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Data Analysis</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Analyzed total expressions of interest and reasons for lost opportunities to identify issues with the Opportunity Feed
                              cards.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/22b81e9fb21c675a6364daa076c0a825.png"
                              alt="Research &amp; Discovery"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Research &amp; Discovery</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Conducted user interviews with 8 legal professionals to understand pain points in the application process.
                            </p>
                            <a
                              href="https://www.dropbox.com/scl/fi/lnclgrdhspmqoinj266hv/Card-readout.pptx?rlkey=xn7d9dkks695e86vy499tlk95&st=y8bhtxxu&dl=0"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-3 text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer"
                            >
                              Readout
                            </a>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-2xl font-semibold mb-6 text-white">Research Findings</h4>
                        <ul className="space-y-4">
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              The opportunity title mattered more to users than the company name.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              In the post-COVID landscape, work arrangement outweighs the physical location in importance.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              The legal role information was difficult to find.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              The job description was highly valued, but too little of it was visible on the card.
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Design Process Section */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Design Process</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          Redesigned the Opportunity Feed cards to highlight the most relevant information, enabling talent to make informed
                          expressions of interest and maximize their chances of landing the role.
                        </p>
                      </div>

                      <div className="mb-12">
                        <h4 className="text-2xl font-bold mb-6 text-white">Ideation</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          <div className="aspect-video overflow-hidden rounded-xl">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/39e5a36fd71a5625157256b65e25af2d.png"
                              alt="Ideation Sketch 1"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="aspect-video overflow-hidden rounded-xl">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/9fc74560c461242a3d69d74d5969a957.png"
                              alt="Ideation Sketch 2"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="aspect-video overflow-hidden rounded-xl">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/80937ae1864cf725885feecab564f4fe.png"
                              alt="Ideation Sketch 3"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-2xl font-bold mb-6 text-white">Before &amp; After</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/3585040ff60709c2c8b20e99264ef026.png"
                                alt="Before Design"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">Before</h4>
                              <p className="text-gray-400 leading-relaxed">
                                Cluttered layout with poor information hierarchy made it difficult for users to quickly assess
                                opportunities.
                              </p>
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/b5bcf467c298a955290e5c2192162867.png"
                                alt="After Design"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">After</h4>
                              <p className="text-gray-400 leading-relaxed">
                                Clean and organized layout with clear visual hierarchy and prominent qualification requirements.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Key Design Improvements */}
                      <h4 className="text-2xl font-bold mb-6 text-white">Key Design Improvements</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-800/50 rounded-xl p-6">
                          <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                            <i className="ri-eye-line text-2xl text-purple-400"></i>
                          </div>
                          <h5 className="text-lg font-semibold mb-3 text-white">Visual Hierarchy</h5>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            Restructured information layout to prioritize key qualifications and role details.
                          </p>
                        </div>
                        <div className="bg-gray-800/50 rounded-xl p-6">
                          <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                            <i className="ri-layout-line text-2xl text-purple-400"></i>
                          </div>
                          <h5 className="text-lg font-semibold mb-3 text-white">Streamlined Layout</h5>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            Better organized content resulting in clear and improved readability.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unique Content for Limiting Expressions Of Interest (ID: 2) */}
                {selectedStudy.id === 2 && (
                  <div>
                    {/* User Interviews & Analysis Section */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Axiom's New Flow</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          Axiom connects legal teams with top talent for everything from everyday in-house needs to complex counsel work. Over
                          time, it has evolved from an analog service to a digital platform while preserving its white-glove experience.
                          The Opportunity Feed marked the first step in empowering lawyers to take control of their careers.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/6ccb96909b0c1890af371239b4200781.png"
                              alt="User Interviews Sessions"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Talent Services Process Changes</h4>
                            <p className="text-gray-400 leading-relaxed">
                              With the introduction of the Opportunity Feed, Talent Services began listing opportunities directly,
                              allowing talent to express interest proactively, rather than waiting for Talent Success to present opportunities.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/55e5aa42771e7857dc310e47ea29d39a.png"
                              alt="User Flows"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Flows</h4>
                            <p className="text-gray-400 leading-relaxed">
                              The card improvements alone weren't enough to resolve the bottleneck Talent Services were experiencing, so we
                              decided to take on the more challenging but higher-impact solution of limiting expressions of interest.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/194d6b4f6d548697b0ae83e84780375a.png"
                              alt="User Personas"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Personas</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Opportunity Feed users could be categorized into three distinct types, with the "Button Spammer" persona being the
                              primary focus when we introduced limits on expressions of interest.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/db3091af5cd9d262c35d443ce4d2c991.png"
                              alt="Simple Journey Map"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Simple Journey Map</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Mapped the user journey to understand how talent discover and apply to opportunities, identifying key decision points
                              and potential friction areas.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Research & Analysis Section */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Research & Analysis</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          Even after the Opportunity Feed card update reduced overall expressions of interest, the "Button Spammer"
                          continued to overuse the "I'm Interested" button—just 10% of talent made up 60% of all submissions.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/387b64729b5a1d1224b402388ec8a4dd.png"
                              alt="User Data"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Data</h4>
                            <p className="text-gray-400 leading-relaxed">
                              As Axiom expanded their talent bench interest in opportunities increased significantly, with over 2,700
                              expressions of interest submitted in August.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/c095ef53a8448cd44fd708b857ff2f3b.png"
                              alt="User Interviews"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Interviews</h4>
                            <p className="text-gray-400 leading-relaxed">
                              I held four interviews with our legal counsel to gather insights on several key areas: general reactions to
                              limiting expressions of interest, approaches to educating users about these limits.
                            </p>
                            <a
                              href="https://www.dropbox.com/scl/fi/11ipxerbdfkts5asfrlqw/EOI-Limiting-Readout.pptx?rlkey=xz0r6kx31ycjz8kq448y53geq&st=kfgi5jw4&dl=0"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-3 text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer"
                            >
                              Readout
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Design Exploration */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Design Exploration</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          I reviewed several real-world examples that demonstrated limits and developed design concepts inspired by them. After
                          evaluating the strengths and weaknesses of each design, I selected the most effective one to test with users.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/bec02326ee354c76d93d49955736373e.png"
                              alt="Design System Components"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Idea #1 After Expression Of Interest</h4>
                            <p className="text-gray-400 leading-relaxed">
                              This design was inspired by Spotify's skip-limit alert, which appears after the skip button is pressed. For Axiom,
                              this timing wouldn't work—users wouldn't know their remaining expressions until after clicking "I'm Interested",
                              leading to confusion and frustration.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/92c5e5ce14772aa74c2e5966e650aec9.png"
                              alt="Design Tokens"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Idea #2 Banner</h4>
                            <p className="text-gray-400 leading-relaxed">
                              This banner design was inspired by Yahoo Sports Fantasy Football, where it effectively grabs attention. However,
                              it only adds value when users are near their limit—otherwise, it becomes unnecessary noise.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/2e6e1669f1c895e70aeea07eb681f1bd.png"
                              alt="Scalable Architecture"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Idea #3 Progress Bar With List</h4>
                            <p className="text-gray-400 leading-relaxed">
                              This concept was inspired by onboarding checklist pattern, using a progress bar with clear steps to complete.
                              It pushes the filters below the fold especially if talent has expressed interest in multiple opportunities.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/1d8ae9e49709203cdd90889748365a0a.png"
                              alt="Guidelines &amp; Documentation"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Idea #4 Simple Progress Bar</h4>
                            <p className="text-gray-400 leading-relaxed">
                              The simple progress bar provides immediate visual feedback that's easy to interpret at a glance. Users can quickly
                              understand how many opportunities they have left without reading detailed text.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-16">
                        <h3 className="text-2xl font-semibold mb-6 text-white">Testable Design</h3>
                        <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                          <p className="text-gray-300 text-lg leading-relaxed">
                            The testable design focused on three key aspects of limiting expressions of interest. First, the Opportunity Interest Counter clearly displayed the number of remaining expressions of interest. Second, the info button highlighted why limiting expressions of interest matters. Finally, the rejection email aimed to soften rejection—acknowledging user value, clarifying the reason for rejection, and directing them back to the Opportunity Feed.
                          </p>
                        </div>

                        {/* Whale Images Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/cbba4ea85c22e228654283f195ed4468.png"
                                alt="Blue Whale in Deep Ocean"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/687a22aedefd3c691bfcf808db49118e.png"
                                alt="Humpback Whale Breaching"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/5fb7dc6245daafc550ee9bc7407d609b.png"
                                alt="Gray Whale Family"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Hippo Images Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/22965d560f0c9aad3ff9d8fded970806.png"
                                alt="Hippopotamus in African River"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/c58e343848a7574f2c5a941490a1ed9b.png"
                                alt="Mother Hippo with Baby"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                        </div>

                        <h4 className="text-2xl font-semibold mb-6 text-white">Research Findings</h4>
                        <ul className="space-y-4">
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              Most lawyers thought that limiting expressions of interest was reasonable and fair.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              All lawyers expected instant communication via email once an expression of interest became available.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              Lawyers become more selective in expressing interest because of the interest counter.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="w-2 h-2 bg-purple-400 rounded-full mt-3 mr-4 flex-shrink-0"></div>
                            <p className="text-gray-300 leading-relaxed">
                              Lawyers thought that 2-3 days was an acceptable amount of time for Talent Services to respond to their expression of interest.
                            </p>
                          </li>
                        </ul>
                      </div>

                      {/* Key Design Improvements Section */}
                      <div className="mt-16">
                        <h4 className="text-2xl font-semibold mb-6 text-white">Key Design Improvements</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-gray-800/50 rounded-xl p-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                              <i className="ri-progress-3-line text-2xl text-purple-400"></i>
                            </div>
                            <h5 className="text-lg font-semibold mb-3 text-white">Progress Visualization</h5>
                            <p className="text-gray-400 text-sm leading-relaxed">
                              Clear progress indicators showing how many expressions of interest they have remaining.
                            </p>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                              <i className="ri-user-line text-2xl text-purple-400"></i>
                            </div>
                            <h5 className="text-lg font-semibold mb-3 text-white">User Education</h5>
                            <p className="text-gray-400 text-sm leading-relaxed">
                              Clear messaging and tooltips that explain why limitations exist and how they benefit users.
                            </p>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                              <i className="ri-restart-line text-2xl text-purple-400"></i>
                            </div>
                            <h5 className="text-lg font-semibold mb-3 text-white">Reset Mechanism</h5>
                            <p className="text-gray-400 text-sm leading-relaxed">
                              Transparent reset system that clearly communicates when users can submit new expressions of interest.
                            </p>
                          </div>
                          <div className="bg-gray-800/50 rounded-xl p-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                              <i className="ri-focus-3-line text-2xl text-purple-400"></i>
                            </div>
                            <h5 className="text-lg font-semibold mb-3 text-white">Selective Behavior</h5>
                            <p className="text-gray-400 text-sm leading-relaxed">
                              Encouraging more thoughtful application decisions through clear limitation feedback.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Final Design Section */}
                      <div className="mt-16">
                        <h3 className="text-3xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                          Final Design
                        </h3>
                        
                        {/* Cow Image */}
                        <div className="mb-8">
                          <div className="aspect-video overflow-hidden rounded-xl">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/20fb2942ac9898684f40f2ef33efeccb.png"
                              alt="Cow in pasture"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                        </div>

                        <div className="bg-gray-800/30 rounded-xl p-8">
                          <p className="text-gray-300 text-lg leading-relaxed">
                            The final implementation successfully balanced user autonomy with platform efficiency. The limitation system reduced low-quality applications while maintaining user satisfaction through clear communication and transparent processes. The design educated users about the value of selective applications, ultimately improving match quality and reducing processing time for the Talent Services team.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unique Content for Sched NPS Score (ID: 3) */}
                {selectedStudy.id === 3 && (
                  <div>
                    {/* Performance & Accessibility */}
                    <div className="mb-12">
                      <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">About Sched</h3>

                      <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                        <p className="text-gray-300 text-lg leading-relaxed">
                          Sched is an all-in-one event management platform that helps organizers plan, schedule, and manage every aspect of their events—from registration and ticketing to attendee engagement. Supporting in-person, virtual, and hybrid formats, Sched offers tools for building personalized schedules, communicating with attendees, and managing sessions and speakers. By focusing on often-overlooked sectors such as education, Sched has positioned itself as a strong alternative to larger players like Cvent.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden rounded-t-lg">
                            <img 
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/112f766165c6e4ca6e9c7518309a9f0f.png"
                              alt="Performance Optimization"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">Education Landscape</h4>
                            <p className="text-gray-400 leading-relaxed">
                              With over 13,000 school districts requiring continuing education for teachers and many running after-school programs, there's a growing need for simple, flexible event management.
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                          <div className="aspect-video overflow-hidden">
                            <img
                              src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/32af1b18a7be6ca468edcd13a678f32c.png"
                              alt="Accessibility Features"
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-semibold mb-3 text-white">User Personas</h4>
                            <p className="text-gray-400 leading-relaxed">
                              Attendees who experience Sched firsthand are 50% more likely to use it for their own events. When they see how effortless and professional Sched makes an event feel, it reinforces their own goals as organizers—saving time and presenting a polished, high-quality experience.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* User Feedback Section */}
                      <div className="mb-16 mt-12">
                        <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                          User Feedback
                        </h3>
                        <p className="text-gray-300 leading-relaxed">
                          To improve our NPS score, we began by analyzing user feedback and creating a journey map to help identify improvement opportunities. While feedback covered all areas of our platform, we chose to focus on the schedule page since it receives the most traffic and offered the greatest potential impact if optimized. We organized user feedback into three distinct categories: session card design, my schedule, and navigation.
                        </p>

                        {/* Golf Course Images Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/19fce60bb66e88772ca11211e10cf3e7.png"
                                alt="Championship Golf Course"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">Group 1 - Card Design Feedback</h4>
                              <p className="text-gray-400 leading-relaxed">
                                The event schedule had several issues: small touch targets, unclear selection indicators, no speaker icon on events you are speaking at, hidden time labels when scrolling, and no color-coded legend.
                              </p>
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/358b42c5ff98ac90c0e7dcf52acad81f.png"
                                alt="Resort Golf Course"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">Group 2 - My Schedule Feedback</h4>
                              <p className="text-gray-400 leading-relaxed">
                                My Schedule was difficult to find since it was buried within the My Profile page. Once there, the link simply redirected back to the same page you were already on.
                              </p>
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/5352ba09587dd6ec1932de4e16e30f15.png"
                                alt="Coastal Golf Course"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">Group 3 - Navigation Feedback</h4>
                              <p className="text-gray-400 leading-relaxed">
                                When a user returned from the session details page, they were taken back to the top of the event schedule instead of the position they left off at.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Cat Images - Two Column Layout */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/91a851fe201914c005409a60e06773fc.png"
                                alt="Orange Tabby Cat"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/e7b16bcf06766331ea7fe2c04f212b73.png"
                                alt="Tuxedo Cat"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Fighter Jet Journey Map Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/b601c8b97d25021eaaf475e68643fef5.png"
                                alt="Fighter Jet Journey Map"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">Journey Map</h4>
                              <p className="text-gray-400 leading-relaxed">
                                After reviewing the customer feedback, I created a journey map to highlight the reported issues and uncover any underlying or unspoken pain points.
                              </p>
                              <a
                                href="https://www.dropbox.com/scl/fi/19xirlyp7c4elk5vthx8j/Journey-Map-Master.png?rlkey=842xdxwkbi9ctx8wnlsmpoz47&st=owvdnxbg&dl=0"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-3 text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer"
                              >
                                View Journey Map
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Group 2 - My Schedule Feedback Section */}
                      <div className="mb-12">
                        <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Addressing Feedback</h3>
                        
                        <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                          <p className="text-gray-300 text-lg leading-relaxed">
                            Once we had identified the key issues, we realized our time to address them was limited. This required us to prioritize—focusing first on the problems with the greatest impact and organizing their resolution in phases. We began with the navigation issues, since those could be handled entirely by the development team, allowing the design team more time to work on the remaining items. Next, we tackled the My Schedule feature, as many of the other fixes depended on its updates, and we wanted to avoid duplicating development work.
                          </p>
                        </div>

                        <h4 className="text-2xl font-semibold mb-6 text-white">My Schedule Design Explorations</h4>
                        <div className="bg-gray-800/30 rounded-xl p-8">
                          <p className="text-gray-300 text-lg leading-relaxed">
                            The My Schedule feature needed to be easily discoverable while maintaining the existing navigation structure. I explored three different approaches to integrate this functionality: adding it as a main tab, incorporating it into the schedule view dropdown, and implementing nested tabs within the Schedule section. Each approach had distinct advantages and challenges that needed to be carefully evaluated against user expectations and technical constraints.
                          </p>
                        </div>

                        {/* Cactus Images Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/9ea5f6d88eb0605826a56f718cb02b24.png"
                                alt="My Schedule As A Main Tab"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">My Schedule As A Main Tab</h4>
                              <p className="text-gray-400 leading-relaxed">
                                In this design exploration I added My Schedule to the main tab navigation. A key principle of tab navigation is to present distinct categories of information. Duplicating content undermines this purpose and disrupts the user's expected workflow.
                              </p>
                            </div>
                          </div>
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/6488dba0c7e7913f5485515b2e3e5a41.png"
                                alt="My Schedule Dropdown"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">My Schedule Dropdown</h4>
                              <p className="text-gray-400 leading-relaxed">
                                In this design exploration, I attempted to add My Schedule to the Schedule View dropdown. However, this approach didn't work since My Schedule isn't actually a schedule view. It also ended up feeling buried and difficult to find.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Third Cactus Image */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                          <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8d714dfbfe8e38874f80bfda35c717ae.png"
                                alt="My Schedule Nested Tabs"
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="p-6">
                              <h4 className="text-xl font-semibold mb-3 text-white">My Schedule Nested Tabs</h4>
                              <p className="text-gray-400 leading-relaxed">
                                I decided to go with the nested tabs because the main "Schedule" tab serves as a clear, centralized hub for all time-related event planning. It aligns with users' mental model, allowing them to easily find all scheduling features (Event Schedule and My Schedule) in one consistent place.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Card Improvements Unclear Selection Indicators Section */}
                        <div className="mt-12">
                          <h4 className="text-2xl font-semibold mb-6 text-white">Card Improvements Unclear Selection Indicators</h4>
                          <div className="bg-gray-800/30 rounded-xl p-8">
                            <p className="text-gray-300 text-lg leading-relaxed">
                              Users often felt unsure about how to select sessions and couldn't easily tell when a session had been added to their schedule. These challenges stemmed from unclear visual cues and limited user guidance. The introduction of the My Schedule tab created a great opportunity to clarify and reinforce the selection process. To address this, we implemented a three-pronged approach.
                            </p>
                          </div>

                          {/* Soda Images Section */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                            <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                              <div className="aspect-video overflow-hidden">
                                <img
                                  src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/258befc315b31fa9ef12a8e089ede928.png"
                                  alt="Old Design Soda"
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="p-6">
                                <h4 className="text-xl font-semibold mb-3 text-white">Old Design</h4>
                                <p className="text-gray-400 leading-relaxed">
                                  In the old design, there was a clear gap in user feedback and guidance. When adding sessions, users received little to no confirmation, leaving them uncertain about whether their actions worked. This lack of clarity caused confusion and frustration. 
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                              <div className="aspect-video overflow-hidden">
                                <img
                                  src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/9c5b74d2b3ca22903ae68f0fdc85d6f9.png"
                                  alt="Checkmark Improvements Soda"
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="p-6">
                                <h4 className="text-xl font-semibold mb-3 text-white">Checkmark Improvements</h4>
                                <p className="text-gray-400 leading-relaxed">
                                  I increased the tap target size to 44px for better accessibility, updated the checkmark color to make it stand out from other icons, and added an animation to confirm when a session is added to or removed from My Schedule. Additionally, I introduced a dynamic badge on the My Schedule tab that briefly expands and contracts each time a session is added, providing subtle but clear feedback.
                                </p>
                                <a
                                  href="https://www.figma.com/proto/v0L9zkOSozqPWjsF0BhANA/Selecting-a-session-consistency?page-id=33%3A2348&node-id=47-2783&p=f&viewport=637%2C163%2C0.25&t=1Fjk4xQALb5EeNoC-1&scaling=scale-down&content-scaling=fixed&starting-point-node-id=47%3A2783"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block mt-3 text-purple-400 hover:text-purple-300 transition-colors duration-300 cursor-pointer"
                                >
                                  View Prototype
                                </a>
                              </div>
                            </div>
                          </div>

                          {/* Additional Soda Images Section */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                            <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                              <div className="aspect-video overflow-hidden">
                                <img
                                  src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/4765ce30862e1f17f225c49c5f57edb4.png"
                                  alt="User Education Soda"
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="p-6">
                                <h4 className="text-xl font-semibold mb-3 text-white">User Education</h4>
                                <p className="text-gray-400 leading-relaxed">
                                  Users were unsure how to add sessions, which made additional guidance essential. To address this, I created an educational modal using UserPilot that walks users through selecting a session. The modal appears when they first access the schedule to provide clear visual and written instructions. 
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                              <div className="aspect-video overflow-hidden">
                                <img
                                  src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/085ce85911378831a7ce7b5c0658c55f.png"
                                  alt="My Schedule Empty State Soda"
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="p-6">
                                <h4 className="text-xl font-semibold mb-3 text-white">My Schedule Empty State</h4>
                                <p className="text-gray-400 leading-relaxed">
                                  The empty state for My Schedule was designed to reinforce how to select a session.  By turning the empty state into a learning opportunity, I strengthened overall user confidence and reduced friction in the session selection flow.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Schedule Consistency Section */}
                          <div className="mt-12">
                            <h4 className="text-2xl font-semibold mb-6 text-white">Schedule Consistency</h4>
                            <div className="bg-gray-800/30 rounded-xl p-8 mb-8">
                              <p className="text-gray-300 text-lg leading-relaxed">
                                There were clear inconsistencies between the mobile app and mobile web designs, which caused confusion for users switching between the two. This was especially problematic at conferences, where unreliable internet made the mobile web more dependable. To address this, I aligned layouts, interactions, and visual patterns across both platforms, ensuring a consistent and intuitive experience regardless of connectivity or device.
                              </p>
                            </div>

                            {/* Piano Images Section */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                              <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                                <div className="aspect-video overflow-hidden">
                                  <img
                                    src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/c947a14e1db5817bec170d7939fa6f65.png"
                                    alt="Old Design Piano"
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div className="p-6">
                                  <h4 className="text-xl font-semibold mb-3 text-white">Old Design</h4>
                                  <p className="text-gray-400 leading-relaxed">
                                    There were inconsistencies in the session selection process. On mobile web, users could select a session directly from the session card, whereas on mobile, they had to navigate to the details page to make a selection. Additionally, there were some styling differences between the two experiences.
                                  </p>
                                </div>
                              </div>
                              <div className="bg-gray-800/30 rounded-xl overflow-hidden">
                                <div className="aspect-video overflow-hidden">
                                  <img
                                    src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/2bb4444b7ee6abf82f5f712f0e563ed4.png"
                                    alt="New Design Piano"
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div className="p-6">
                                  <h4 className="text-xl font-semibold mb-3 text-white">New Design</h4>
                                  <p className="text-gray-400 leading-relaxed">
                                    The redesigned interface unified both platforms with consistent interaction patterns and visual elements. Users can now select sessions directly from cards on both mobile web and app, eliminating confusion and creating a seamless experience regardless of platform choice.
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Speaker Schedule Section */}
                            <div className="mt-12">
                              <h4 className="text-2xl font-semibold mb-6 text-white">Speaker Schedule</h4>
                              <div className="bg-gray-800/30 rounded-xl p-8">
                                <p className="text-gray-400 text-lg leading-relaxed">
                                  Speakers were struggling to locate their own speaking sessions and often tried to add sessions that overlapped with their presentation times. This created scheduling conflicts that disrupted their overall conference experience. To solve this, I implemented several improvements. First, I added a speaker icon next to any session where the user was presenting, making those events easy to identify at a glance. Second, we introduced a filter on the My Schedule page, allowing speakers to quickly view and edit their speaking engagements. Finally, I faded out sessions that conflicted with their speaking times, preventing double bookings and helping them manage their time more effectively.

These changes made it simpler for speakers to navigate their schedules, avoid conflicts, and focus on their engagements with confidence.
                                </p>
                              </div>

                              {/* Microphone Images */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                                <div className="aspect-video overflow-hidden rounded-xl">
                                  <img
                                    src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/ce8e0068712b99cb149917925d7ca37e.png"
                                    alt="Professional Studio Microphone"
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div className="aspect-video overflow-hidden rounded-xl">
                                  <img
                                    src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/04f15afeecd3064c4e5d69ba783ee493.png"
                                    alt="Vintage Ribbon Microphone"
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                              </div>

                              {/* Key Design Improvements Section */}
                              <div className="mt-16">
                                <h4 className="text-2xl font-semibold mb-6 text-white">
                                  Key Design Improvements
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="bg-gray-800/50 rounded-xl p-6">
                                    <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                                      <i className="ri-calendar-schedule-line text-2xl text-purple-400"></i>
                                    </div>
                                    <h5 className="text-lg font-semibold mb-3 text-white">Enhanced Schedule Navigation</h5>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                      Redesigned navigation structure with nested tabs and improved information hierarchy to help users find and manage their sessions more efficiently.
                                    </p>
                                  </div>
                                  <div className="bg-gray-800/50 rounded-xl p-6">
                                    <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                                      <i className="ri-checkbox-circle-line text-2xl text-purple-400"></i>
                                    </div>
                                    <h5 className="text-lg font-semibold mb-3 text-white">Clear Selection Feedback</h5>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                      Implemented visual indicators, animations, and educational modals to provide clear feedback when users add sessions to their schedule.
                                    </p>
                                  </div>
                                  <div className="bg-gray-800/50 rounded-xl p-6">
                                    <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                                      <i className="ri-smartphone-line text-2xl text-purple-400"></i>
                                    </div>
                                    <h5 className="text-lg font-semibold mb-3 text-white">Mobile-Web Consistency</h5>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                      Aligned mobile app and mobile web experiences to eliminate confusion and provide seamless interaction across all platforms.
                                    </p>
                                  </div>
                                  <div className="bg-gray-800/50 rounded-xl p-6">
                                    <div className="w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-lg mb-4">
                                      <i className="ri-mic-line text-2xl text-purple-400"></i>
                                    </div>
                                    <h5 className="text-lg font-semibold mb-3 text-white">Speaker Experience</h5>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                      Added speaker icons, conflict prevention, and dedicated speaker filters to help presenters manage their speaking engagements effectively.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Sched Final Design Section */}
                              <div className="mt-16">
                                <h3 className="text-3xl font-bold mb-8 text-left text-purple-400">Sched Final Design</h3>
                                
                                <div className="aspect-video overflow-hidden rounded-xl mb-8">
                                  <img
                                    src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/a5295ade4a84b4576f0b755fe32179eb.png"
                                    alt="Tulip field"
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>

                                <div className="bg-gray-800/30 rounded-xl p-8">
                                  <p className="text-gray-300 text-lg leading-relaxed">
                                    The redesigned schedule experience successfully addressed all major user pain points while maintaining Sched's core functionality. By implementing clear visual feedback, improving navigation structure, and ensuring cross-platform consistency, we created a more intuitive and reliable event management experience. The enhanced speaker features and conflict prevention mechanisms particularly strengthened our value proposition for event organizers and presenters. These improvements contributed to a measurable increase in user satisfaction and positioned Sched as a more competitive alternative in the event management space.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Call to Action */}
                <div className="mt-16 text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setSelectedStudy(null)}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    View More Projects
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
